import React from 'react';
import { useResolvedMediaUrl } from '../hooks/useResolvedMediaUrl';
import { AD_BILLING_OPTIONS, AD_BUTTON_TEXTS, AD_CTA_OPTIONS, AD_PLACEMENT_OPTIONS, AdBillingModel, AdCtaType, buttonTextFor, estimateAdResults } from '../utils/adActions';

/** Image from a stored media key (or full URL), resolved to a signed URL. */
export const ResolvedImg: React.FC<{ src?: string | null; alt?: string; style?: React.CSSProperties }> = ({ src, alt = '', style }) => {
  const url = useResolvedMediaUrl(src || '');
  return url ? <img src={url} alt={alt} style={style} /> : null;
};

type FieldStyles = { [key: string]: React.CSSProperties };

interface CtaProps {
  actionType: AdCtaType;
  actionValue: string;
  buttonText: string;
  onChange: (next: { actionType: AdCtaType; actionValue: string; buttonText: string }) => void;
  styles: FieldStyles;
}

const hintStyle: React.CSSProperties = { fontSize: 12, color: '#6b7280', marginTop: 4 };

export const AdCtaFields: React.FC<CtaProps> = ({ actionType, actionValue, buttonText, onChange, styles }) => {
  const set = (patch: Partial<{ actionType: AdCtaType; actionValue: string; buttonText: string }>) =>
    onChange({ actionType, actionValue, buttonText, ...patch });
  const needsValue = actionType === 'url' || actionType === 'call' || actionType === 'whatsapp';
  const valueLabel = actionType === 'url' ? 'Website link' : actionType === 'whatsapp' ? 'WhatsApp number' : 'Phone number';
  return (
    <>
      <div style={styles.formGroup}>
        <label style={styles.label}>Call to action *</label>
        <select
          value={actionType}
          onChange={(e) => {
            const type = e.target.value as AdCtaType;
            const switchingKind = (type === 'url') !== (actionType === 'url');
            set({ actionType: type, buttonText: buttonTextFor(type, buttonText), ...(switchingKind ? { actionValue: '' } : {}) });
          }}
          style={styles.input}
        >
          {AD_CTA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {actionType === 'message' && <div style={hintStyle}>People who tap the button open a Belivah chat with you.</div>}
      </div>

      {needsValue && (
        <div style={styles.formGroup}>
          <label style={styles.label}>{valueLabel} *</label>
          <input
            type={actionType === 'url' ? 'url' : 'tel'}
            inputMode={actionType === 'url' ? 'url' : 'tel'}
            value={actionValue}
            onChange={(e) => set({ actionValue: e.target.value })}
            required
            style={styles.input}
            placeholder={actionType === 'url' ? 'example.com or https://example.com' : '+2348012345678'}
          />
          {actionType !== 'url' && <div style={hintStyle}>Include the country code, e.g. +234 for Nigeria.</div>}
        </div>
      )}

      {actionType !== 'none' && (
        <div style={styles.formGroup}>
          <label style={styles.label}>Button text *</label>
          <select value={buttonTextFor(actionType, buttonText)} onChange={(e) => set({ buttonText: e.target.value })} style={styles.input} required>
            {AD_BUTTON_TEXTS[actionType].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      )}
    </>
  );
};

interface PlacementProps {
  placements: string[];
  onChange: (next: string[]) => void;
}

export const AdPlacementFields: React.FC<PlacementProps> = ({ placements, onChange }) => {
  const all = AD_PLACEMENT_OPTIONS.map((o) => o.value);
  const allSelected = all.every((p) => placements.includes(p));
  const toggle = (p: string) => onChange(placements.includes(p) ? placements.filter((x) => x !== p) : [...placements, p]);
  const row: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', cursor: 'pointer' };
  return (
    <div>
      <label style={{ ...row, fontWeight: 600 }}>
        <input type="checkbox" checked={allSelected} onChange={() => onChange(allSelected ? [] : all)} style={{ marginTop: 3 }} />
        <span>Everywhere (recommended)<div style={hintStyle}>Reach the most people across Belivah.</div></span>
      </label>
      {AD_PLACEMENT_OPTIONS.map((o) => (
        <label key={o.value} style={{ ...row, paddingLeft: 24 }}>
          <input type="checkbox" checked={placements.includes(o.value)} onChange={() => toggle(o.value)} style={{ marginTop: 3 }} />
          <span>{o.label}<div style={hintStyle}>{o.hint}</div></span>
        </label>
      ))}
    </div>
  );
};

/** Billing model choice plus a live estimate of what the budget buys. */
export const AdBillingFields: React.FC<{ value: AdBillingModel; onChange: (v: AdBillingModel) => void; budgetUsd: number }> = ({ value, onChange, budgetUsd }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontWeight: 600, marginBottom: 8 }}>How do you want to pay?</div>
    <div style={{ display: 'grid', gap: 8 }}>
      {AD_BILLING_OPTIONS.map((o) => (
        <label key={o.value} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: 10, borderRadius: 10, cursor: 'pointer', border: `1px solid ${value === o.value ? '#0B6B6F' : '#CFE3E3'}`, background: value === o.value ? '#EAF6F6' : 'transparent' }}>
          <input type="radio" name="ad-billing" checked={value === o.value} onChange={() => onChange(o.value)} style={{ marginTop: 3 }} />
          <span>{o.label}<div style={hintStyle}>{o.hint}</div></span>
        </label>
      ))}
    </div>
    <div style={{ marginTop: 8, fontSize: 13, color: '#0B4F51' }}>
      Your budget of ${Number(budgetUsd || 0).toLocaleString()} gets you {estimateAdResults(budgetUsd, value)}. You only pay for what you get; unused budget stays in your wallet.
    </div>
  </div>
);
